import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AccountType,
  BankRecordType,
  CheckClassification,
  ClassificationMetadataCancellationTypeEnum,
  CustomerFundsClassification,
  FinancialInstitutions,
  ReconciliationStatus,
  RevenueAccountsClassification,
  TransactionDirection
} from '@melio/payments-recon-api-client-ts';
import useQueryParams from './useQueryParams.hook';

type Values = {
  from: string;
  to: string;
  after: string;
  before: string;
  accountType: AccountType;
  bankRecordType: Array<BankRecordType>;
  financialInstitution: FinancialInstitutions;
  reconciliationStatus: ReconciliationStatus;
  classification: Array<
    | CheckClassification
    | CustomerFundsClassification
    | RevenueAccountsClassification
  >;
  cancellationType: Array<ClassificationMetadataCancellationTypeEnum>;
  paymentMethod: 'ach' | 'check' | 'card';
  sortBy: string;
  sortDirection: string;
  recordId: string;
  fromAmount?: number;
  toAmount?: number;
  direction?: TransactionDirection;
  mid?: Array<string>;
  description: string;
  paymentId: string;
  checkSerialNumber: string;
};

export const useRecoTableQueryState = () => {
  const history = useHistory();
  const queryParams = useQueryParams();

  const setQueryState = useCallback(
    (changes: Partial<Values>, historyAction: 'push' | 'replace' = 'push') => {
      Object.entries(changes).forEach(([property, value]) => {
        if (value || Number.isFinite(value)) {
          queryParams.set(property, String(value));
        } else {
          queryParams.delete(property);
        }
      });

      history[historyAction]({ search: queryParams.toString() });
    },
    [queryParams, history]
  );

  const setDefaultQueryState = useCallback(
    (defaultValues: Partial<Values>) => {
      const changes: Partial<Values> = {};

      Object.entries(defaultValues).forEach(([property, value]) => {
        if (!queryParams.has(property)) changes[property] = value;
      });

      setQueryState(changes, 'replace');
    },
    [queryParams, setQueryState]
  );

  const decodedParams = Object.fromEntries(queryParams.entries());

  const mid = decodedParams['mid']?.split(',').filter(Boolean) as Array<string>;

  const bankRecordType = decodedParams['bankRecordType']
    ?.split(',')
    .filter(Boolean) as Array<BankRecordType>;

  const classification = decodedParams['classification']
    ?.split(',')
    .filter(Boolean) as Array<
    | CheckClassification
    | CustomerFundsClassification
    | RevenueAccountsClassification
  >;

  const cancellationType = decodedParams['cancellationType']
    ?.split(',')
    .filter(Boolean) as Array<ClassificationMetadataCancellationTypeEnum>;

  const queryState: Partial<Values> = {
    ...decodedParams,
    bankRecordType,
    mid,
    classification,
    cancellationType,
    fromAmount: parseFloat(decodedParams['fromAmount']) || undefined,
    toAmount: parseFloat(decodedParams['toAmount']) || undefined
  };

  return { queryState, setQueryState, setDefaultQueryState };
};
